import { buttonStyle } from 'components/buttons/buttonStyle';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper.tsx';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput.tsx';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip.tsx';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { format, isBefore, subDays } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useInvoice } from 'pages/invoices/hooks/useInvoice.ts';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useRecalculateInvoiceDateMutation } from 'store/api/invoices.ts';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features.ts';
import { InvoiceData, InvoiceForm, InvoiceStatusType, PrepaymentDataListItem } from 'types/invoices';
import { cn } from 'utils/cn';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations.ts';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';

/** Fixed shares of a row: a cell keeps its third or half of the width whatever it holds. */
const THIRD = 'max-w-1/3 basis-1/3';
const HALF = 'max-w-1/2 basis-1/2';

interface Props {
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    invoice: InvoiceData;
    submitInvoice: () => void;
    existingPrepayments?: PrepaymentDataListItem[];
    /** The card's header offers the recalculation, as the new design's does: the form leaves out its Update. */
    recalculatesInHeader?: boolean;
}

/** Under SERVICE_DATE_CONTROL, an invoice with no service date takes the end of its service period as its saved one. */
export function useServiceDateAutofill() {
    const { control, resetField } = useFormContext<InvoiceForm>();
    const isServiceDateControlEnabled = useIsFeatureEnabled(FeatureFlag.SERVICE_DATE_CONTROL);
    const [serviceDate, maxInvoiceTimecardOrExpenseDate] = useWatch({
        control,
        name: ['serviceDate', 'maxInvoiceTimecardOrExpenseDate'],
    });
    useEffect(() => {
        if (isServiceDateControlEnabled && !serviceDate && maxInvoiceTimecardOrExpenseDate) {
            resetField('serviceDate', { defaultValue: maxInvoiceTimecardOrExpenseDate });
        }
    }, [isServiceDateControlEnabled, serviceDate, maxInvoiceTimecardOrExpenseDate, resetField]);
}

/**
 * Restates the invoice date, service period and due date from what the invoice bills, saved at once, and puts the
 * dates it returns into the form.
 */
export function useRecalculateInvoiceDates(invoiceId: number) {
    const { setValue, clearErrors } = useFormContext<InvoiceForm>();
    const [recalculateInvoiceDate] = useRecalculateInvoiceDateMutation();
    return useCallback(async () => {
        try {
            const updatedInvoice = await recalculateInvoiceDate({ invoiceId }).unwrap();
            // Update the form fields with the recalculated dates
            if (updatedInvoice.serviceStartDate) {
                setValue('serviceStartDate', updatedInvoice.serviceStartDate);
                clearErrors('serviceStartDate');
            }
            if (updatedInvoice.serviceEndDate) {
                setValue('serviceEndDate', updatedInvoice.serviceEndDate);
                clearErrors('serviceEndDate');
            }
            if (updatedInvoice.invoiceDate) {
                setValue('invoiceDate', updatedInvoice.invoiceDate);
                clearErrors('invoiceDate');
            }
            if (updatedInvoice.dueDate) {
                setValue('dueDate', updatedInvoice.dueDate);
                clearErrors('dueDate');
            }
        } catch (error) {
            console.error('Failed to recalculate invoice date:', error);
        }
    }, [recalculateInvoiceDate, invoiceId, setValue, clearErrors]);
}

const InvoiceDetailsCard: React.FC<Props> = ({
    classifiers,
    isInViewMode,
    invoice,
    submitInvoice,
    existingPrepayments,
    recalculatesInHeader = false,
}) => {
    const { control, setValue } = useFormContext<InvoiceForm>();
    const { isCredit, isFinalized, isCorrection } = useInvoice(invoice);
    const isCreditedInvoice = invoice?.invoiceStatusTypeCode === InvoiceStatusType.CREDITED;
    const handleRecalculateDate = useRecalculateInvoiceDates(invoice.id);
    const dateFormat = useEffectiveDateFormat();
    const isUserPrivilegedToUpdateInvoiceNumber = useHasPermissions(['UPDATE.INVOICES.INVOICE_NUMBER']);
    const isDisplayInPaymentCurrencyEnabled = useIsFeatureEnabled(
        FeatureFlag.INVOICE_FIELDS_VALUES_IN_PAYMENT_CURRENCY
    );
    const [invoiceCurrencyType, paymentCurrencyType, isPaymentCurrencyDifferent, serviceDate, serviceStartDate] =
        useWatch({
            control,
            name: [
                'invoiceCurrencyType',
                'paymentCurrencyType',
                'isPaymentCurrencyDifferent',
                'serviceDate',
                'serviceStartDate',
            ],
        });

    const isIssueDateEnabled = useIsFeatureEnabled(FeatureFlag.ISSUE_DATE_FIELD_ON_INVOICE);
    // Up to fullhd the fields divide the whole row; past it they stop growing so they don't stretch.
    const dateFieldClassName = cn(
        'min-w-0 flex-1',
        isIssueDateEnabled ? 'fullhd:max-w-[25%]' : 'fullhd:max-w-[33.333%]'
    );
    const isInvoiceDateLockedToCurrentDate = !isInViewMode && !!invoice?.invoiceDateLockedToCurrentDate;
    const isPaymentCurrencySelected =
        !!paymentCurrencyType && !!invoiceCurrencyType && paymentCurrencyType.value !== invoiceCurrencyType.value;

    useServiceDateAutofill();

    const handleCheckBoxChange = useCallback(
        (value: boolean) => {
            setValue('isDisplayedInPaymentCurrency', isDisplayInPaymentCurrencyEnabled && value);

            if (!value && paymentCurrencyType?.value !== invoiceCurrencyType?.value) {
                setValue('paymentCurrencyType', invoiceCurrencyType);
                submitInvoice();
            }
        },
        [invoiceCurrencyType, paymentCurrencyType?.value, setValue, submitInvoice, isDisplayInPaymentCurrencyEnabled]
    );

    const hasInvoicePayments =
        !!invoice.invoicePayments?.length || existingPrepayments?.some((prepayment) => prepayment.isConnected);
    // The backend only sends a reference number when the tenant has reference number generation
    // configured (or Netvisor enabled), so its presence is the single source of truth for visibility.
    const showReferenceNumber = !!invoice.referenceNumber;

    const validateServiceEndDate = useCallback(
        (value: unknown) => {
            if (typeof value !== 'string' || !serviceStartDate) {
                return Promise.resolve(true);
            }

            if (isBefore(new Date(value), new Date(serviceStartDate))) {
                const dayBefore = subDays(new Date(serviceStartDate), 1);
                return Promise.resolve(
                    t('validation.must-be-date-after', {
                        greaterThan: isNewDesignCopy() ? format(dayBefore, dateFormat) : dayBefore.toLocaleDateString(),
                    })
                );
            }

            return Promise.resolve(true);
        },
        [serviceStartDate, dateFormat]
    );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-nowrap gap-4">
                <div className={dateFieldClassName}>
                    <DateInput
                        control={control}
                        name="invoiceDate"
                        fullWidth
                        label="invoices.general-tab.invoice-details.invoice-date"
                        readonly={isInViewMode}
                        disabled={isInvoiceDateLockedToCurrentDate}
                        tooltip={
                            isInvoiceDateLockedToCurrentDate ? (
                                <InfoTooltip text="invoices.tooltips.invoice-date-locked-to-current-date" />
                            ) : undefined
                        }
                        required
                        onChange={() => {
                            setValue('invoiceDateManuallyUpdated', true);
                            submitInvoice();
                        }}
                    />
                </div>
                {recalculatesInHeader ? null : (
                    <FeatureFlagged action="show" flag={FeatureFlag.CUSTOM_SERVICE_PERIOD_RECALC_LOGIC}>
                        <Button
                            {...buttonStyle(
                                { variant: 'outlined' },
                                'hover:bg-grey-100 hover:border-divider self-end text-[14px] leading-6 font-normal'
                            )}
                            disabled={isInViewMode}
                            onClick={() => {
                                void handleRecalculateDate();
                            }}
                        >
                            <FormattedMessage id="update" />
                        </Button>
                    </FeatureFlagged>
                )}
                {isIssueDateEnabled ? (
                    <div className={dateFieldClassName}>
                        <DateInput
                            control={control}
                            name="issueDate"
                            fullWidth
                            label="invoices.general-tab.invoice-details.issue-date"
                            readonly={isInViewMode}
                            onChange={() => {
                                setValue('issueDateManuallyUpdated', true);
                                submitInvoice();
                            }}
                        />
                    </div>
                ) : null}
                {isCreditedInvoice ? null : (
                    <div className={dateFieldClassName}>
                        <DateInput
                            control={control}
                            name="dueDate"
                            fullWidth
                            label="invoices.general-tab.invoice-details.due-date"
                            readonly={isInViewMode}
                            onChange={() => {
                                setValue('dueDateManuallyUpdated', true);
                                submitInvoice();
                            }}
                        />
                    </div>
                )}
            </div>
            <div className="flex flex-nowrap gap-4">
                <div className={THIRD}>
                    <DateInput
                        control={control}
                        name="serviceStartDate"
                        fullWidth
                        label="invoices.general-tab.invoice-details.service-start-date"
                        readonly={isInViewMode}
                        onChange={submitInvoice}
                    />
                </div>
                <div className={THIRD}>
                    <DateInput
                        control={control}
                        name="serviceEndDate"
                        fullWidth
                        label="invoices.general-tab.invoice-details.service-end-date"
                        readonly={isInViewMode}
                        onChange={submitInvoice}
                        validate={validateServiceEndDate}
                    />
                </div>
            </div>
            <FeatureFlagged action="show" flag={FeatureFlag.SERVICE_DATE_CONTROL}>
                <div className="flex flex-nowrap gap-4">
                    <div className={THIRD}>
                        <DateInput
                            control={control}
                            name="serviceDate"
                            label="invoices.general-tab.invoice-details.service-date"
                            readonly={isInViewMode}
                            fullWidth
                            onChange={submitInvoice}
                            required
                        />
                    </div>
                    <div className={THIRD}>
                        {!serviceDate ? (
                            <DateInput
                                control={control}
                                name="maxInvoiceTimecardOrExpenseDate"
                                label="invoices.general-tab.invoice-details.service-period-end-date"
                                disabled
                                readonly
                                wrapperClassName="mt-[3px]"
                            />
                        ) : null}
                    </div>
                    {/* for alignment */}
                    <div className={THIRD} />
                </div>
            </FeatureFlagged>
            {showReferenceNumber ? (
                <div className="flex flex-col gap-4 md:flex-row">
                    <TextInput
                        control={control}
                        name="invoiceNumber"
                        label="invoices.general-tab.invoice-details.invoice-number"
                        readonly={isInViewMode}
                        disabled={!isUserPrivilegedToUpdateInvoiceNumber}
                        required={isFinalized}
                        onBlur={submitInvoice}
                    />
                    <InputWrapper
                        name="referenceNumber"
                        label="invoices.general-tab.invoice-details.reference-number"
                        hasErrors={false}
                        tooltip={<InfoTooltip text="invoices.general-tab.invoice-details.reference-number-tooltip" />}
                    >
                        <Input
                            id="referenceNumber"
                            value={invoice.referenceNumber ?? ''}
                            disabled
                            readOnly
                            className="bg-paper"
                        />
                    </InputWrapper>
                </div>
            ) : (
                <TextInput
                    control={control}
                    name="invoiceNumber"
                    label="invoices.general-tab.invoice-details.invoice-number"
                    readonly={isInViewMode}
                    disabled={!isUserPrivilegedToUpdateInvoiceNumber}
                    required={isFinalized}
                    onBlur={submitInvoice}
                />
            )}
            <div className="flex flex-row gap-4">
                <TextAreaInput
                    control={control}
                    name="invoiceDescription"
                    label="invoices.general-tab.invoice-details.invoice-description"
                    readonly={isInViewMode}
                    onBlur={submitInvoice}
                />
                <TextAreaInput
                    control={control}
                    name="invoiceDescriptionLocalLanguage"
                    label="invoices.general-tab.invoice-details.invoice-description-local-language"
                    readonly={isInViewMode}
                    onBlur={submitInvoice}
                />
            </div>
            <div className="flex flex-row gap-4">
                <TextAreaInput
                    control={control}
                    name="outlineDescription"
                    label="invoices.general-tab.invoice-details.outline-description"
                    readonly={isInViewMode}
                    onBlur={submitInvoice}
                />
                <TextAreaInput
                    control={control}
                    name="outlineDescriptionLocalLanguage"
                    label="invoices.general-tab.invoice-details.outline-description-local-language"
                    readonly={isInViewMode}
                    onBlur={submitInvoice}
                />
            </div>
            <TextAreaInput
                control={control}
                name="additionalAddress"
                label="invoices.general-tab.invoice-details.additional-address"
                readonly={isInViewMode}
                onBlur={submitInvoice}
            />
            <TextAreaInput
                control={control}
                name="additionalInfo"
                label="invoices.general-tab.invoice-details.additional-info"
                maxLength={4000}
                readonly={isInViewMode}
                onBlur={submitInvoice}
            />
            <div className="flex flex-nowrap gap-4">
                <div className={HALF}>
                    <SelectInput
                        control={control}
                        setValue={setValue}
                        name="invoiceCurrencyType"
                        options={
                            (getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                                label: string;
                                value: string;
                            }[]) ?? []
                        }
                        getOptionIdentifier="value"
                        getOptionLabel="label"
                        label="invoices.general-tab.invoice-details.invoice-currency"
                        // CDP-948 Disable invoice currency editing
                        // required
                        // onChange={submitInvoice}
                        readonly={true}
                    />
                </div>
            </div>
            <div className="flex flex-nowrap content-center gap-4">
                <div className={HALF}>
                    <CheckboxInput
                        name="isPaymentCurrencyDifferent"
                        control={control}
                        label={'invoices.general-tab.invoice-details.use-different-payment-currency'}
                        disabled={hasInvoicePayments}
                        readonly={isInViewMode || isCredit || isCorrection}
                        wrapperClassName="items-start"
                        onChange={handleCheckBoxChange}
                        rightLabelComponent={
                            hasInvoicePayments ? (
                                <InfoTooltip
                                    className="mt-[6px] ml-[6px]"
                                    color={'secondary'}
                                    text="invoices.tooltips.cannot-change-payment-currency"
                                />
                            ) : null
                        }
                    />
                    {isPaymentCurrencyDifferent && isPaymentCurrencySelected && (
                        <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_FIELDS_VALUES_IN_PAYMENT_CURRENCY}>
                            <CheckboxInput
                                name="isDisplayedInPaymentCurrency"
                                control={control}
                                label={'invoices.general-tab.invoice-details.display-in-payment-currency'}
                                readonly={isInViewMode || isCredit || isCorrection}
                                onChange={() => {
                                    if (isPaymentCurrencySelected) {
                                        submitInvoice();
                                    }
                                }}
                            />
                        </FeatureFlagged>
                    )}
                </div>
                <div className={HALF}>
                    {isPaymentCurrencyDifferent ? (
                        <SelectInput
                            control={control}
                            setValue={setValue}
                            name="paymentCurrencyType"
                            onChange={submitInvoice}
                            options={
                                (
                                    getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                                        label: string;
                                        value: string;
                                    }[]
                                )?.filter((option) => option.value !== invoiceCurrencyType?.value) ?? []
                            }
                            getOptionIdentifier="value"
                            getOptionLabel="label"
                            label="invoices.general-tab.invoice-details.payment-currency"
                            required={isPaymentCurrencyDifferent}
                            disabled={hasInvoicePayments}
                            readonly={isInViewMode || isCredit || isCorrection}
                            clearable={false}
                            wrapperClassName="mt-[5px]"
                        />
                    ) : null}
                </div>
            </div>
            <FeatureFlagged action="show" flag={FeatureFlag.BASE_CURRENCY_CONVERSION_DATE_CONTROL}>
                <DateInput
                    control={control}
                    name="baseCurrencyConversionDate"
                    label="invoices.general-tab.invoice-details.base-currency-conversion-date"
                    readonly={isInViewMode}
                    onChange={submitInvoice}
                />
            </FeatureFlagged>
            <FeatureFlagged action="show" flag={FeatureFlag.PAYMENT_CURRENCY_CONVERSION_DATE_CONTROL}>
                <DateInput
                    control={control}
                    name="paymentCurrencyConversionDate"
                    label="invoices.general-tab.invoice-details.payment-currency-conversion-date"
                    readonly={isInViewMode}
                    onChange={submitInvoice}
                />
            </FeatureFlagged>
        </div>
    );
};

export default InvoiceDetailsCard;
